import { Link } from 'react-router-dom';
import { useI18n, useTranslation, LOCALES } from '../i18n';
import LangSwitcher from '../components/LangSwitcher';
import ThemeToggle from '../components/ThemeToggle';
import Search from '../components/Search';

export default function HomePage() {
  const t = useTranslation();
  const { locale } = useI18n();
  return (
    <div className="home">
      <header className="home-header">
        <div className="home-brand">
          <img src="/favicon.png" alt="" width={24} height={24} />
          <strong>{t('site.title')}</strong>
        </div>
        <div className="home-header-actions">
          <a href="https://sepolia.metha.life" target="_blank" rel="noreferrer" className="home-link-quiet">
            {t('nav.dapp')} ↗
          </a>
          <LangSwitcher lang={locale} />
          <ThemeToggle />
        </div>
      </header>
      <main className="home-main">
        <h1>{t('site.title')}</h1>
        <p className="home-tagline">{t('site.tagline')}</p>

        <div className="home-search-wrap">
          <Search />
        </div>

        <div className="home-cta-grid">
          <Link to={`/${locale}/guides/lab`} className="home-cta">
            <span>{t('home.cta.lab')}</span>
            <span aria-hidden>→</span>
          </Link>
          <Link to={`/${locale}/guides/auditor`} className="home-cta">
            <span>{t('home.cta.auditor')}</span>
            <span aria-hidden>→</span>
          </Link>
          <Link to={`/${locale}/overview`} className="home-cta">
            <span>{t('home.cta.learn')}</span>
            <span aria-hidden>→</span>
          </Link>
        </div>

        <div className="home-grid">
          <section>
            <h3>{t('home.section.start')}</h3>
            <ul>
              <li><Link to={`/${locale}/how-it-works/value-loop`}>The value loop</Link></li>
              <li><Link to={`/${locale}/architecture/contracts`}>See the 6 contracts</Link></li>
              <li><a href="https://sepolia.metha.life" target="_blank" rel="noreferrer">Try the dapp ↗</a></li>
            </ul>
          </section>
          <section>
            <h3>{t('home.section.status')}</h3>
            <ul>
              <li>Sepolia v3 deployed 2026-05-13</li>
              <li>6 contracts verified on Etherscan</li>
              <li>Mainnet: audit-gated</li>
            </ul>
          </section>
        </div>

        <section className="home-recent">
          <h3>{t('home.section.recent')}</h3>
          <ul>
            <li>Legal Due Diligence v1.0 (2026-05)</li>
            <li>Whitepaper v6 (2026-05)</li>
            <li>DVBC Case Study v1.4.1 deposited to Zenodo</li>
          </ul>
        </section>
      </main>
      <style>{css}</style>
    </div>
  );
}

const _LOCALES = LOCALES; // appease unused-import in case Locale type goes unused above
void _LOCALES;

const css = `
.home {
  max-width: 880px;
  margin: 0 auto;
  padding: 24px;
}
.home-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 64px;
}
.home-brand { display: flex; align-items: center; gap: 10px; }
.home-header-actions { display: flex; align-items: center; gap: 12px; }
.home-link-quiet { color: var(--c-muted); font-size: 0.9rem; }
.home-link-quiet:hover { color: var(--c-fg); text-decoration: none; }

.home-main h1 {
  font-family: var(--font-serif);
  font-size: 2.5rem;
  letter-spacing: -0.02em;
  margin: 0;
}
.home-tagline {
  font-size: 1.15rem;
  color: var(--c-muted);
  margin: 8px 0 32px;
}

.home-search-wrap {
  margin-bottom: 32px;
}

.home-cta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-bottom: 48px;
}
.home-cta {
  display: flex;
  justify-content: space-between;
  padding: 16px 20px;
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  color: var(--c-fg);
  text-decoration: none;
  transition: border-color 0.15s, transform 0.15s;
}
.home-cta:hover {
  border-color: var(--c-accent);
  text-decoration: none;
  transform: translateY(-1px);
}

.home-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-top: 32px;
}
@media (max-width: 700px) { .home-grid { grid-template-columns: 1fr; } }

.home-grid h3, .home-recent h3 {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--c-muted);
  margin-bottom: 8px;
}
.home-grid ul, .home-recent ul { padding-left: 18px; margin: 0; }
.home-grid li, .home-recent li { margin: 4px 0; color: var(--c-fg); }

.home-recent { margin-top: 32px; }

/* --- Phone: let the header wrap, tighten spacing, shrink the hero --- */
@media (max-width: 600px) {
  .home { padding: 16px; }
  .home-header {
    margin-bottom: 40px;
    flex-wrap: wrap;
    gap: 12px;
  }
  .home-header-actions { flex-wrap: wrap; }
  .home-main h1 { font-size: 2rem; }
  .home-tagline { font-size: 1.05rem; }
}
`;
